import { useEffect, useState } from "react"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTable } from "primereact/datatable"
import { ProgressSpinner } from "primereact/progressspinner"
import { deletePreLiqDetail, getPreLiquidationItemDetails } from "../../servicesHooks/Honorarium/useHonorariumPreLiquidationService"
import {
    DeletePreLiqDetailResultDTO,
    HonorariumPreLiquidationDetailDTO,
    HonorariumPreLiquidationDetailTypeCode,
    HonorariumPreLiquidationItemDTO,
} from "../../dto/HonorariumPreLiquidationDTO"
import { parseApiError } from "../../services/apiError"
import { formatMoney } from "../../utils/money"
import { confirmDelete } from "../../components/ConfirmDelete/confirmDelete"
import SignedAmount from "../../components/HonorariumMovement/SignedAmount"
import MovementTypeTag from "../../components/HonorariumMovement/MovementTypeTag"
import useNotify from "../../hooks/useNotify"

interface PreLiquidationItemDetailsProps {
    item: HonorariumPreLiquidationItemDTO;
    /** La pre-liquidación está activa y el ítem pendiente: sus honorarios se pueden eliminar. */
    editable: boolean;
    onDetailDeleted: (result: DeletePreLiqDetailResultDTO) => void;
}

/** Cómo se nombra un detalle al confirmar: paciente y práctica; sin ellos (pre-liquidaciones viejas), su descripción. */
function detailLabel(detail: HonorariumPreLiquidationDetailDTO): string {
    const parts = [detail.patientName, detail.nomenclatorDescription].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : detail.description;
}

/** Detalles de un ítem de la pre-liquidación, dentro de su fila desplegada. */
export default function PreLiquidationItemDetails({ item, editable, onDetailDeleted }: PreLiquidationItemDetailsProps) {
    const notify = useNotify();
    const itemId = item.honorariumPreLiquidationItem;

    const [details, setDetails] = useState<HonorariumPreLiquidationDetailDTO[]>([]);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        getPreLiquidationItemDetails(itemId)
            .then(data => { if (!cancelled) setDetails(data); })
            .catch((error: unknown) => {
                parseApiError(error); // loguea errorCode y path para cruzarlo con el backend
                if (!cancelled) notify.error('No se pudieron cargar los detalles del ítem. Intentá de nuevo.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [itemId, reloadKey, notify]);

    function handleDelete(detail: HonorariumPreLiquidationDetailDTO) {
        confirmDelete({
            header: '¿Eliminar detalle?',
            message: `Se va a eliminar «${detailLabel(detail)}» (${formatMoney(detail.professionalAmount)} a pagar). `
                + 'Vuelve a quedar pendiente para otra pre-liquidación.',
            onAccept: async () => {
                try {
                    const result = await deletePreLiqDetail(detail.honorariumPreLiquidationDetail);
                    // Si el ítem sigue existiendo, sus detalles cambiaron; si no, la fila desaparece al recargar los ítems
                    if (!result.itemRemoved && !result.preLiquidationCancelled) setReloadKey(key => key + 1);
                    onDetailDeleted(result);
                } catch (error: unknown) {
                    // El backend manda un mensaje seguro para el usuario: el motivo de negocio o el error genérico
                    notify.error(parseApiError(error).message);
                }
            },
        });
    }

    if (loading) {
        return (
            <div className="md-loading" role="status">
                <ProgressSpinner strokeWidth="4" />
                <span>Cargando…</span>
            </div>
        );
    }

    return (
        <div className="preliq-detail__item-details">
            <DataTable value={details} dataKey="honorariumPreLiquidationDetail" className="crudTable" emptyMessage="Sin detalles cargados.">
                <Column
                    field="movementType"
                    header="Tipo de movimiento"
                    body={(row: HonorariumPreLiquidationDetailDTO) => (
                        // Las pre-liquidaciones anteriores a los movimientos no tienen tipo: su descripción es lo que las explica
                        <MovementTypeTag label={row.movementType} detail={row.movementType ? row.movementDetail : row.description} />
                    )}
                />
                <Column
                    field="payerTypeName"
                    header="Pagador"
                    body={(row: HonorariumPreLiquidationDetailDTO) => row.payerTypeName ?? '—'}
                />
                <Column
                    field="patientName"
                    header="Paciente"
                    body={(row: HonorariumPreLiquidationDetailDTO) => row.patientName ?? '—'}
                />
                <Column
                    field="nomenclatorDescription"
                    header="Práctica"
                    body={(row: HonorariumPreLiquidationDetailDTO) => row.nomenclatorDescription ?? '—'}
                />
                <Column
                    field="amount"
                    header="Monto total"
                    body={(row: HonorariumPreLiquidationDetailDTO) => <SignedAmount amount={row.amount} format={formatMoney} />}
                    className="text-right"
                    headerClassName="col-right"
                />
                <Column
                    field="honorariumPercentage"
                    header="% honorario"
                    body={(row: HonorariumPreLiquidationDetailDTO) =>
                        row.honorariumPercentage != null ? `${row.honorariumPercentage}%` : '—'
                    }
                    className="text-center"
                    headerClassName="col-center"
                />
                <Column
                    field="professionalAmount"
                    header="A pagar"
                    body={(row: HonorariumPreLiquidationDetailDTO) => <SignedAmount amount={row.professionalAmount} format={formatMoney} strong />}
                    className="text-right"
                    headerClassName="col-right"
                />
                {editable && (
                    <Column
                        header=""
                        style={{ width: '64px' }}
                        className="text-center"
                        body={(row: HonorariumPreLiquidationDetailDTO) =>
                            row.type === HonorariumPreLiquidationDetailTypeCode.HONORARIO && (
                                <Button
                                    text
                                    rounded
                                    icon="pi pi-trash"
                                    className="md-icon-btn"
                                    aria-label={`Eliminar el detalle ${detailLabel(row)}`}
                                    tooltip="Eliminar"
                                    tooltipOptions={{ position: 'left' }}
                                    onClick={() => handleDelete(row)}
                                />
                            )
                        }
                    />
                )}
            </DataTable>
            {!editable && (
                <p className="md-help preliq-detail__note">
                    <i className="pi pi-info-circle" aria-hidden="true" /> Los ítems liquidados no se pueden modificar.
                </p>
            )}
        </div>
    );
}
